/**
 * Audio Bubble Engine - Media-Lifecycle Compatible
 *
 * Purpose:
 * - Interactive audio hotspots on panels
 * - Desktop: Hover to play, leave to pause
 * - Mobile: Tap to toggle play/pause
 * - Single-Medium Enforcement via StoryMediaManager
 * - Panel-aware: Stops when panel deactivated
 * - On-Demand Loading (first hover/tap loads audio)
 * - Fade-In (200ms), immediate stop on panel switch
 * - Resume with -1s rewind for context
 * - Progress ring animation (SVG strokeDashoffset)
 *
 * Media-Lifecycle Integration:
 * - Implements Module Contract (init/start/pause/stop/cleanup)
 * - Registers with StoryMediaManager
 * - Reacts to Panel Events (via MediaManager)
 *
 * Dependencies: StoryMediaManager
 * File: assets/js/story/audio-bubbles.js
 */

(function(window) {
  'use strict';

  /* ----------------------------------------------------------
     1. Audio Bubbles Module Class
     ---------------------------------------------------------- */

  class AudioBubblesModule {
    constructor() {
      // Module Contract Properties
      this.name = 'audio-bubbles';
      this.type = 'audio';
      this.isPlaying = false;
      this.panel = null;
      this.element = null;  // Current bubble element

      // Internal State
      this.bubbles = [];
      this.currentAudio = null;
      this.currentBubble = null;
      this.onCloseCallback = null;  // Callback for when bubble closes
      this._activeCaption = null;   // M2: caption element currently shown

      // Mobile Detection
      this.isTouchDevice = this.detectTouch();

      // Initialize
      this.initializeModule();
    }

    /* ----------------------------------------------------------
       Module Initialization
       ---------------------------------------------------------- */

    initializeModule() {
      // Wait for DOM
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.registerWithManager());
      } else {
        this.registerWithManager();
      }
    }

    registerWithManager() {
      // Register with MediaManager
      if (window.StoryMediaManager) {
        window.StoryMediaManager.register(this);

        if (window.location.search.includes('debug=story')) {
          console.log('[AudioBubbles] Registered with MediaManager');
        }
      } else {
        console.warn('[AudioBubbles] StoryMediaManager not found - running in standalone mode');
        // Fallback: Initialize immediately without MediaManager
        this.initStandalone();
      }

      // Listen for external audio play events (e.g., from panorama hotspots)
      document.addEventListener('story:audio:play', (e) => {
        console.log('[AudioBubbles] Received story:audio:play event:', e.detail);
        this.handleExternalAudioRequest(e.detail);
      });

      console.log('[AudioBubbles] Event listener for story:audio:play registered');
    }

    /**
     * Handle audio play request from external source (e.g., panorama hotspot)
     */
    handleExternalAudioRequest(detail) {
      console.log('[AudioBubbles] handleExternalAudioRequest called:', detail);

      if (!detail || !detail.src) {
        console.error('[AudioBubbles] Missing detail or src!');
        return;
      }

      // Store callback for when audio closes
      this.onCloseCallback = detail.onClose || null;

      // Request permission from MediaManager
      if (window.StoryMediaManager) {
        const allowed = window.StoryMediaManager.requestPlay(this);
        if (!allowed) {
          if (window.location.search.includes('debug=story')) {
            console.warn('[AudioBubbles] External play request denied');
          }
          return;
        }
      }

      // Create floating audio bubble for external audio
      this.createFloatingBubble(detail);
    }

    /**
     * Create a floating audio bubble (for external audio like panorama hotspots)
     */
    createFloatingBubble(detail) {
      console.log('[AudioBubbles] createFloatingBubble called:', detail);

      // Remove existing floating bubble if any
      this.closeFloatingBubble();

      // Create bubble container
      const bubble = document.createElement('div');
      bubble.className = 'audio-bubble audio-bubble--floating';
      bubble.innerHTML = `
        <div class="audio-bubble__content">
          <div class="audio-bubble__info">
            <span class="audio-bubble__title">${this.escapeHtml(detail.title || 'Audio')}</span>
          </div>
          <button class="audio-bubble__close" aria-label="Schließen">×</button>
        </div>
        <div class="audio-bubble__controls">
          <button class="audio-bubble__play-pause" aria-label="Pause">
            <svg class="audio-bubble__icon audio-bubble__icon--pause" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="7" y="6" width="3.5" height="12" rx="1" fill="currentColor"/>
              <rect x="13.5" y="6" width="3.5" height="12" rx="1" fill="currentColor"/>
            </svg>
            <svg class="audio-bubble__icon audio-bubble__icon--play" viewBox="0 0 24 24" aria-hidden="true" style="display:none">
              <path d="M9 7l10 5-10 5V7z" fill="currentColor"/>
            </svg>
          </button>
          <div class="audio-bubble__progress-bar">
            <div class="audio-bubble__progress-fill"></div>
          </div>
        </div>
      `;

      // Create audio element
      const audio = new Audio(detail.src);
      audio.preload = 'auto';
      bubble._audio = audio;
      bubble._isFloating = true;

      // Add to DOM
      document.body.appendChild(bubble);
      this.floatingBubble = bubble;

      // Event handlers
      const closeBtn = bubble.querySelector('.audio-bubble__close');
      const playPauseBtn = bubble.querySelector('.audio-bubble__play-pause');
      const progressBar = bubble.querySelector('.audio-bubble__progress-bar');
      const progressFill = bubble.querySelector('.audio-bubble__progress-fill');
      const pauseIcon = bubble.querySelector('.audio-bubble__icon--pause');
      const playIcon = bubble.querySelector('.audio-bubble__icon--play');

      // Close button
      closeBtn.addEventListener('click', () => {
        this.closeFloatingBubble();
      });

      // Play/Pause toggle
      playPauseBtn.addEventListener('click', () => {
        if (audio.paused) {
          audio.play();
          pauseIcon.style.display = '';
          playIcon.style.display = 'none';
        } else {
          audio.pause();
          pauseIcon.style.display = 'none';
          playIcon.style.display = '';
        }
      });

      // Progress bar click to seek
      progressBar.addEventListener('click', (e) => {
        const rect = progressBar.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        audio.currentTime = percent * audio.duration;
      });

      // Update progress
      audio.addEventListener('timeupdate', () => {
        if (audio.duration) {
          const percent = (audio.currentTime / audio.duration) * 100;
          progressFill.style.width = percent + '%';
        }
      });

      // Auto-close on end
      audio.addEventListener('ended', () => {
        this.closeFloatingBubble();
      });

      // Start playback
      this.currentAudio = audio;
      this.currentBubble = bubble;
      this.element = bubble;
      this.isPlaying = true;

      audio.play().then(() => {
        this.fadeIn(audio);
        bubble.classList.add('audio-bubble--playing');
      }).catch((err) => {
        if (window.location.search.includes('debug=story')) {
          console.warn('[AudioBubbles] Floating bubble autoplay failed:', err);
        }
      });

      // Animate in
      requestAnimationFrame(() => {
        bubble.classList.add('audio-bubble--visible');
      });
    }

    /**
     * Close floating audio bubble
     */
    closeFloatingBubble() {
      if (!this.floatingBubble) return;

      const bubble = this.floatingBubble;
      const audio = bubble._audio;

      // Fade out audio
      if (audio && !audio.paused) {
        this.fadeOut(audio, () => {
          audio.pause();
          audio.src = '';
        });
      }

      // Animate out
      bubble.classList.remove('audio-bubble--visible');
      bubble.classList.add('audio-bubble--closing');

      setTimeout(() => {
        bubble.remove();
      }, 300);

      this.floatingBubble = null;
      this.currentAudio = null;
      this.currentBubble = null;
      this.element = null;
      this.isPlaying = false;

      // Notify MediaManager
      if (window.StoryMediaManager) {
        window.StoryMediaManager.notifyStop(this);
      }

      // Call onClose callback (e.g., to resume panorama background audio)
      if (this.onCloseCallback) {
        this.onCloseCallback();
        this.onCloseCallback = null;
      }

      if (window.location.search.includes('debug=story')) {
        console.log('[AudioBubbles] Floating bubble closed');
      }
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    /* ----------------------------------------------------------
       Media-Lifecycle Hooks (Module Contract)
       ---------------------------------------------------------- */

    init(panel) {
      // Called when panel is activated
      this.panel = panel;

      // Find all bubbles in this panel
      this.bubbles = Array.from(panel.querySelectorAll('.audio-bubble'));

      if (this.bubbles.length === 0) {
        return; // No bubbles in this panel
      }

      if (window.location.search.includes('debug=story')) {
        console.log('[AudioBubbles] Init in panel:', this.bubbles.length, 'bubbles found');
      }

      // Setup each bubble
      this.bubbles.forEach(bubble => this.initBubble(bubble));

      // Autostart (Phase 1.2): a bubble marked data-audio-autostart plays
      // when its panel first opens — once per page load, not on re-entry.
      const autoBubble = this.bubbles.find(b => b.hasAttribute('data-audio-autostart'));
      if (autoBubble && !autoBubble._abAutostarted) {
        autoBubble._abAutostarted = true;
        this.start(autoBubble);
      }
    }

    start(bubble) {
      // Called when user interacts with bubble (hover/click)
      // This is the entry point for playing audio

      // Request permission from MediaManager
      if (window.StoryMediaManager) {
        const allowed = window.StoryMediaManager.requestPlay(this);
        if (!allowed) {
          if (window.location.search.includes('debug=story')) {
            console.warn('[AudioBubbles] Play request denied by MediaManager');
          }
          return;
        }
      }

      // Start audio playback
      this.startBubbleAudio(bubble, { resume: true });
    }

    pause() {
      // Called by MediaManager when another medium starts or panel deactivated
      this.stopBubbleAudio(this.currentBubble, { pauseOnly: true });

      // Notify MediaManager we've stopped
      if (window.StoryMediaManager && this.isPlaying) {
        window.StoryMediaManager.notifyStop(this);
      }

      this.isPlaying = false;
    }

    stop() {
      // Full stop with reset
      this.stopBubbleAudio(this.currentBubble, { pauseOnly: false });

      // Notify MediaManager
      if (window.StoryMediaManager && this.isPlaying) {
        window.StoryMediaManager.notifyStop(this);
      }

      this.isPlaying = false;
    }

    cleanup() {
      // Free resources (optional, called on panel deactivation)
      this.stop();
      this.currentAudio = null;
      this.currentBubble = null;
      this.element = null;
      this.bubbles = [];
      this.panel = null;
    }

    /* ----------------------------------------------------------
       Mobile Detection
       ---------------------------------------------------------- */

    detectTouch() {
      return (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
      );
    }

    /* ----------------------------------------------------------
       Bubble Initialization
       ---------------------------------------------------------- */

    // B-3 (2026-07-25): Das Namens-/Zeit-Label ist auf der Bubble zentriert
    // (translateX(-50%)) und wächst beim Abspielen um die Laufzeit
    // („Name · 0:06 / 0:20"). Bei randnahen Bubbles ragt es sonst über
    // .story-scrolly-wrapper (overflow-x:hidden) und wird abgeschnitten. Hier
    // horizontal in den Viewport klammern — EINZEILIG (kein max-width/Umbruch,
    // damit die vertikale D3b-Zeilen-Abwechslung mit top/bottom unberührt
    // bleibt); verschoben wird nur der translateX-Anteil.
    clampLabelIntoView(label) {
      if (!label) return;
      const bubble = label.closest('.audio-bubble');
      if (!bubble) return;
      const M = 8;
      const lw = label.getBoundingClientRect().width;        // translateX-invariant
      const br = bubble.getBoundingClientRect();
      const naturalLeft = br.left + br.width / 2 - lw / 2;   // left bei reiner Zentrierung
      let shift = 0;
      if (naturalLeft < M) shift = Math.round(M - naturalLeft);
      else if (naturalLeft + lw > window.innerWidth - M) {
        shift = -Math.round(naturalLeft + lw - (window.innerWidth - M));
      }
      if (label._abLastShift === shift) return;              // kein Layout-Thrash
      label._abLastShift = shift;
      label.style.transform = shift ? ('translateX(calc(-50% + ' + shift + 'px))') : '';
    }

    initBubble(bubble) {
      const audioSrc = bubble.dataset.audio;
      if (!audioSrc) return;

      // Idempotency guard (Phase 1.2): init(panel) runs on every panel
      // activation — incl. re-entry on scroll-back. Without this, the
      // interaction listeners below would stack on the same element.
      if (bubble._abInitialized) return;
      bubble._abInitialized = true;

      // Create Audio object (metadata only - lazy loading)
      const audio = new Audio(audioSrc);
      audio.preload = 'metadata';  // Only load duration, not full audio
      audio.volume = 0;
      audio.muted = !!this._muted; // globaler Mute gilt auch für Spät-Initialisierte (B3)
      bubble._audio = audio;
      bubble._audioLoaded = false;  // Track if audio is loaded

      // Setup progress ring (if present)
      const progressCircle = bubble.querySelector('.audio-bubble__progress');
      if (progressCircle) {
        this.setupProgressRing(progressCircle, audio);
      }

      // Natürliches Audio-Ende (Sichtung 2026-07-23): Playing-Klasse ablegen
      // und den MediaManager informieren (sonst bleibt die Atmo geduckt und
      // die Bubble pulsiert weiter). Caption bleibt bewusst stehen — das
      // letzte Häppchen zeigt der ended-Handler in renderSyncCaption.
      audio.addEventListener('ended', () => {
        bubble.classList.remove('audio-bubble--playing');
        if (this.currentBubble === bubble) {
          this.isPlaying = false;
          if (window.StoryMediaManager) {
            window.StoryMediaManager.notifyStop(this);
          }
        }
      });

      // Live time label (Phase 1.2) — only if the bubble has a .audio-bubble__time
      const timeEl = bubble.querySelector('.audio-bubble__time');
      if (timeEl) {
        const labelEl = bubble.querySelector('.audio-bubble__label');
        audio.addEventListener('loadedmetadata', () => {
          timeEl.textContent = this.formatTime(audio.duration);
          this.clampLabelIntoView(labelEl);
        });
        // B-3 (2026-07-25, David-Entscheidung): NUR AUF MOBIL (Touch) während
        // der Wiedergabe die reine verstrichene Zeit zeigen — NICHT
        // „current / total". Das „/ total" verbreiterte das Label beim
        // Abspielen (aus „Name · 0:58" wurde „Name · 0:06 / 0:20") und schnitt
        // es bei randnahen Bubbles auf schmalen Screens ab (Davids iOS-Befund;
        // „0:12" ist so breit wie die Ruhe-Gesamtdauer „0:58" → die Box wächst
        // nicht mehr, schneidet baulich nie ab, ohne Positions-Messung —
        // Fortschritt/Restzeit stehen am Ring). DESKTOP behält bewusst die
        // volle „current / total"-Anzeige (breiter Screen, kein Platzproblem).
        // Die clampLabelIntoView-Klammer bleibt für beide als Sicherheitsnetz.
        audio.addEventListener('timeupdate', () => {
          if (!audio.duration || audio.duration === Infinity) return;
          timeEl.textContent = this.isTouchDevice
            ? this.formatTime(audio.currentTime)
            : this.formatTime(audio.currentTime) + ' / ' + this.formatTime(audio.duration);
          this.clampLabelIntoView(labelEl);
        });
      }

      // INTERACTION: Desktop vs Mobile
      if (!this.isTouchDevice) {
        // Desktop: Hover to play
        bubble.addEventListener('mouseenter', () => {
          this.start(bubble);  // Use lifecycle method
        });

        bubble.addEventListener('mouseleave', () => {
          this.pause();  // Use lifecycle method
        });

      } else {
        // Mobile: Tap to toggle
        bubble.addEventListener('click', () => {
          const audio = bubble._audio;
          if (!audio) return;

          // If this bubble is playing → pause
          if (audio === this.currentAudio && this.isPlaying) {
            this.pause();
          } else {
            this.start(bubble);
          }
        });
      }
    }

    /* ----------------------------------------------------------
       Progress Ring Setup
       ---------------------------------------------------------- */

    setupProgressRing(circle, audio) {
      const radius = circle.r.baseVal.value;
      const circumference = 2 * Math.PI * radius;
      circle._circumference = circumference;

      // Initial state
      circle.style.strokeDasharray = `${circumference} ${circumference}`;
      circle.style.strokeDashoffset = circumference;

      // Update progress
      audio.addEventListener('timeupdate', () => {
        if (!audio.duration || audio.duration === Infinity) return;
        const progress = audio.currentTime / audio.duration;
        const offset = circumference * (1 - progress);
        circle.style.strokeDashoffset = offset;
      });

      // Reset on end
      audio.addEventListener('ended', () => {
        circle.style.strokeDashoffset = circumference;
      });
    }

    /* ----------------------------------------------------------
       Audio Playback Control
       ---------------------------------------------------------- */

    startBubbleAudio(bubble, { resume = true } = {}) {
      if (!bubble) return;

      const audio = bubble._audio;
      if (!audio) return;

      // SINGLE-WINNER im Modul: der MediaManager pausiert nur ANDERE Module —
      // Bubble→Bubble-Wechsel innerhalb dieses Moduls muss hier selbst stoppen
      // (Davids Live-Befund 2026-07-23: Mehrfach-Playback).
      if (this.currentBubble && this.currentBubble !== bubble) {
        this.stopBubbleAudio(this.currentBubble, { pauseOnly: true });
      }

      // Update module state
      this.currentAudio = audio;
      this.currentBubble = bubble;
      this.element = bubble;
      this.isPlaying = true;

      // ON-DEMAND LOADING: Check if audio loaded
      if (!bubble._audioLoaded) {
        bubble.classList.add('audio-bubble--loading');

        // Event: Audio ready to play. GUARD: bis canplaythrough feuert, kann
        // längst eine andere Bubble dran sein — dann NICHT mehr starten
        // (Ursache des Mehrfach-Playbacks bei langsamem Netz).
        const onCanPlay = () => {
          bubble._abOnCanPlay = null;
          bubble._audioLoaded = true;
          bubble.classList.remove('audio-bubble--loading');
          if (this.currentBubble !== bubble || !this.isPlaying) return;
          this.actuallyStartAudio(audio, bubble, resume);
        };

        // Ältere Pending-Listener derselben Bubble entsorgen (Doppel-Start)
        if (bubble._abOnCanPlay) {
          audio.removeEventListener('canplaythrough', bubble._abOnCanPlay);
        }
        bubble._abOnCanPlay = onCanPlay;
        audio.addEventListener('canplaythrough', onCanPlay, { once: true });

        // Trigger load
        audio.load();
        return;
      }

      // Audio already loaded, play immediately
      this.actuallyStartAudio(audio, bubble, resume);
    }

    // Sicherheits-Sweep: JEDES andere Bubble-Audio im Dokument hart pausieren.
    // Deckt auch Streuner ab, die an keinem Modul-Zustand mehr hängen
    // (Race-Reste, Panel-Wechsel-Leichen). Billig: nur DOM-Query + paused-Check.
    silenceOtherBubbleAudio(keepBubble) {
      document.querySelectorAll('.audio-bubble').forEach((b) => {
        if (b === keepBubble || !b._audio) return;
        if (!b._audio.paused) {
          b._audio.pause();
          b._audio.volume = 0;
        }
        b.classList.remove('audio-bubble--playing', 'audio-bubble--loading');
      });
    }

    actuallyStartAudio(audio, bubble, resume) {
      this.silenceOtherBubbleAudio(bubble);

      // Nach dem Ende: nächster Start beginnt VON VORN (Davids Sichtung
      // 2026-07-22) — sonst würde die Resume-Logik nur die letzte Sekunde
      // wiederholen.
      if (audio.ended ||
          (audio.duration && audio.duration !== Infinity && audio.currentTime >= audio.duration - 0.25)) {
        audio.currentTime = 0;
      } else if (resume && audio.currentTime > 1.0) {
        // -1s REWIND: Resume 1 second back for context
        audio.currentTime = Math.max(0, audio.currentTime - 1.0);
      } else if (!resume) {
        audio.currentTime = 0;
      }

      // Play safely (handle autoplay blocking)
      if (audio.paused) {
        audio.play().catch((error) => {
          // Autoplay blocked - user needs to interact
          if (window.location.search.includes('debug=story')) {
            console.log('[AudioBubbles] Autoplay blocked, waiting for user interaction');
          }
        });
      }

      // Update UI
      bubble.classList.add('audio-bubble--playing');
      this.showCaption(bubble);

      // Fade in (200ms)
      this.fadeIn(audio);
    }

    stopBubbleAudio(bubble, { pauseOnly = true, fadeOut = false } = {}) {
      if (!bubble) return;

      const audio = bubble._audio;
      if (!audio) return;

      // Pending-Ladevorgang abbrechen: sonst startet der canplaythrough-
      // Listener die Bubble später doch noch (Mehrfach-Playback-Race).
      if (bubble._abOnCanPlay) {
        audio.removeEventListener('canplaythrough', bubble._abOnCanPlay);
        bubble._abOnCanPlay = null;
        bubble.classList.remove('audio-bubble--loading');
      }

      const doStop = () => {
        audio.pause();
        audio.volume = 0;

        if (!pauseOnly) {
          audio.currentTime = 0;
        }

        // Update UI
        bubble.classList.remove('audio-bubble--playing');
        this.hideCaption();

        // Reset state
        if (audio === this.currentAudio) {
          this.currentAudio = null;
          this.currentBubble = null;
          this.element = null;
        }
      };

      // Clear any existing fade (nur das dieses Audios — Fades sind pro Audio)
      clearInterval(audio._abFadeIv);

      if (fadeOut && audio.volume > 0) {
        // Fade out then stop (200ms)
        this.fadeOut(audio, doStop);
      } else {
        // Immediate stop
        doStop();
      }
    }

    /* ----------------------------------------------------------
       Fade Functions
       ---------------------------------------------------------- */

    // Fades laufen PRO AUDIO (audio._abFadeIv), nicht über ein geteiltes
    // Intervall: sonst killt der Fade-In von Bubble B den Fade-Out-Callback
    // von Bubble A — A bliebe dann leise weiterspielend hängen.
    fadeIn(audio, targetVolume = 1) {
      clearInterval(audio._abFadeIv);
      audio.volume = 0;

      // 0.25 every 50ms = 4 steps = 200ms for 0→1
      const step = targetVolume / 4;
      audio._abFadeIv = setInterval(() => {
        if (audio.volume < targetVolume - step) {
          audio.volume = Math.min(targetVolume, audio.volume + step);
        } else {
          audio.volume = targetVolume;
          clearInterval(audio._abFadeIv);
        }
      }, 50);
    }

    fadeOut(audio, callback) {
      clearInterval(audio._abFadeIv);
      const startVolume = audio.volume;

      if (startVolume <= 0) {
        // Already silent
        if (callback) callback();
        return;
      }

      // Symmetrical to fadeIn: 4 steps × 50ms = 200ms
      const step = startVolume / 4;
      audio._abFadeIv = setInterval(() => {
        if (audio.volume > step) {
          audio.volume = Math.max(0, audio.volume - step);
        } else {
          audio.volume = 0;
          clearInterval(audio._abFadeIv);
          if (callback) callback();
        }
      }, 50);
    }

    /* ----------------------------------------------------------
       Caption & Time Label  (Iteration 1, Phase 1.2)
       ---------------------------------------------------------- */

    // AP-B/B3 (2026-07-23): globaler Mute vom MediaManager-Fan-out.
    // muted-Property statt Volume — kollidiert nicht mit den Fades.
    setMuted(muted) {
      this._muted = !!muted;
      document.querySelectorAll('.audio-bubble').forEach((b) => {
        if (b._audio) b._audio.muted = this._muted;
      });
    }

    // Format seconds → "m:ss"
    formatTime(seconds) {
      if (!seconds || isNaN(seconds) || seconds === Infinity) return '0:00';
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60);
      return m + ':' + (s < 10 ? '0' : '') + s;
    }

    // M2 Synchron-Text: write the bubble's data-caption into the
    // .audio-bubble-caption container of the same panel.
    // Karaoke-Variante (Sperrgebiet, David 2026-07-22): trägt der Bubble
    // data-caption-sync="1", erscheint das Transkript satz-/gruppenweise,
    // linear über die Gesamtdauer des Audios getaktet (Näherung ohne
    // Wort-Zeitstempel). Opt-in — Bestandstouren unverändert.
    showCaption(bubble) {
      const panel = bubble && bubble.closest ? bubble.closest('.story-panel') : null;
      const text = bubble ? bubble.getAttribute('data-caption') : null;
      if (!panel || !text) return;
      const el = panel.querySelector('.audio-bubble-caption');
      if (!el) return;
      if (bubble.dataset.captionSync === '1') {
        this.renderSyncCaption(bubble, el, text);
      } else {
        el.classList.remove('audio-bubble-caption--sync');
        el._syncSpans = null;
        el.textContent = text;
      }
      this.positionCaptionNearBubble(bubble, el);
      el.classList.add('audio-bubble-caption--visible');
      this._activeCaption = el;
    }

    // Caption in Bubble-Nähe platzieren (Davids Sichtung 2026-07-22):
    // direkt UNTER der Bubble, bei Platzmangel darüber — nicht irgendwo
    // am Panel-Rand. Nur für frei platzierte sg-Stimmen (.sg-scrolly__voices,
    // --x/--y); andernorts (Bestandstouren) bleibt die Autor-Position.
    positionCaptionNearBubble(bubble, el) {
      const voices = bubble.closest('.sg-scrolly__voices');
      const panel = bubble.closest('.story-panel');
      if (!voices || !panel) {
        el.classList.remove('audio-bubble-caption--near');
        return;
      }
      el.classList.add('audio-bubble-caption--near');

      // Kandidaten-Reihenfolge unter → über → rechts → links; gewählt wird
      // die erste Position, die im Panel liegt und KEINE Bubble überdeckt
      // (Sichtung 2026-07-22: Caption überlagerte in Kap. 3/4 andere
      // Stimmen). Messung nach jedem Setzen (Layout ist dann aktuell).
      const GAP = 14;
      const pr = panel.getBoundingClientRect();
      const br = bubble.getBoundingClientRect();
      // Auch die (absolut positionierten, aus dem Button ragenden) Labels
      // zählen als Hindernis — sie sitzen im sg-Kontext ÜBER den Bubbles
      // (Sichtung 2026-07-23), die Caption darf sie nicht überdecken.
      const bubbles = Array.prototype.slice.call(
        voices.querySelectorAll('.audio-bubble, .audio-bubble__label')
      ).map((b) => b.getBoundingClientRect());

      const place = (mode) => {
        el.style.top = 'auto';
        el.style.bottom = 'auto';
        if (mode === 'unter' || mode === 'ueber') {
          // Breitenbewusste Klammerung (Live-Befund D3a, 2026-07-24): die
          // alte 14–86%-Klammer kannte die Caption-Breite nicht — auf
          // schmalen Screens ragte die Tafel links aus dem Bild (x=-52,
          // Text angeschnitten). Jetzt in px: Mitte so begrenzt, dass die
          // halbe Tafelbreite plus Rand immer im Panel bleibt.
          const capW = Math.min(el.offsetWidth || 0, pr.width - 16);
          const cxPx = (br.left + br.right) / 2 - pr.left;
          const geklammert = Math.max(capW / 2 + 8, Math.min(pr.width - capW / 2 - 8, cxPx));
          el.style.left = geklammert + 'px';
          el.style.transform = 'translateX(-50%)';
          if (mode === 'unter') el.style.top = (br.bottom - pr.top + GAP) + 'px';
          else el.style.bottom = (pr.height - (br.top - pr.top) + GAP) + 'px';
        } else {
          el.style.top = (br.top + br.height / 2 - pr.top) + 'px';
          if (mode === 'rechts') {
            el.style.left = (br.right - pr.left + GAP) + 'px';
            el.style.transform = 'translateY(-50%)';
          } else {
            el.style.left = (br.left - pr.left - GAP) + 'px';
            el.style.transform = 'translate(-100%, -50%)';
          }
        }
      };

      const collides = () => {
        const er = el.getBoundingClientRect();
        const imPanel = er.left >= pr.left + 4 && er.right <= pr.right - 4 &&
                        er.top >= pr.top + 4 && er.bottom <= pr.bottom - 4;
        if (!imPanel) return true;
        return bubbles.some((r) =>
          er.left < r.right && er.right > r.left && er.top < r.bottom && er.bottom > r.top);
      };

      const modes = ['unter', 'ueber', 'rechts', 'links'];
      for (let i = 0; i < modes.length; i++) {
        place(modes[i]);
        if (!collides()) return;
      }
      place('unter'); // Fallback, falls nichts kollisionsfrei ist
    }

    hideCaption() {
      if (this._activeCaption) {
        this._activeCaption.classList.remove('audio-bubble-caption--visible');
        this._activeCaption = null;
      }
    }

    // Caption in Häppchen zerlegen (Vertrag 2026-07-22/23, präzisiert):
    // Enthält der Text ABSÄTZE, steuert ALLEIN der Autor die Grenzen —
    // jede Zeile ist WORTWÖRTLICH ein Häppchen, egal wie lang (auch 20
    // Wörter). Nur Captions OHNE Absätze trennt die Runtime automatisch
    // an Satzenden (.!?…) und teilt Überlängen (>12 Wörter) in ~10-Wort-
    // Gruppen. Kein Lookbehind (alte Safaris).
    splitCaptionChunks(text) {
      const t = String(text);
      if (/\n/.test(t)) {
        const lines = t.split(/\n+/).map((z) => z.trim()).filter(Boolean);
        return lines.length ? lines : [t];
      }
      const parts = t.match(/[^.!?…\n]+[.!?…]*[\s]*/g) || [t];
      const chunks = [];
      parts.forEach((part) => {
        const words = part.trim().split(/\s+/).filter(Boolean);
        if (!words.length) return;
        if (words.length <= 12) {
          chunks.push(words.join(' '));
          return;
        }
        const groups = Math.ceil(words.length / 10);
        const per = Math.ceil(words.length / groups);
        for (let i = 0; i < words.length; i += per) {
          chunks.push(words.slice(i, i + per).join(' '));
        }
      });
      return chunks.length ? chunks : [t];
    }

    renderSyncCaption(bubble, el, text) {
      const chunks = this.splitCaptionChunks(text);
      el.textContent = '';
      el.classList.add('audio-bubble-caption--sync');
      el._syncSpans = chunks.map((chunk) => {
        const span = document.createElement('span');
        span.className = 'audio-bubble-caption__chunk';
        span.textContent = chunk;
        el.appendChild(span);
        return span;
      });
      this.updateSyncCaption(bubble, el);

      // timeupdate einmal pro Bubble binden (das Audio-Objekt lebt am Bubble)
      if (!bubble._captionSyncBound && bubble._audio) {
        bubble._captionSyncBound = true;
        bubble._audio.addEventListener('timeupdate', () => {
          if (this.currentBubble === bubble && this._activeCaption && this._activeCaption._syncSpans) {
            this.updateSyncCaption(bubble, this._activeCaption);
          }
        });
        // Am Audio-Ende: letztes Häppchen zeigen (rollendes Fenster — deckt
        // auch Quellen ohne brauchbare duration ab, z. B. duration=Infinity).
        // currentBubble-Guard wie beim timeupdate (Ultrareview bug_007):
        // sonst blitzt beim Nachlaufen von Bubble A das letzte Häppchen
        // der inzwischen aktiven Bubble B auf.
        bubble._audio.addEventListener('ended', () => {
          if (this.currentBubble === bubble && this._activeCaption && this._activeCaption._syncSpans) {
            const spans = this._activeCaption._syncSpans;
            spans.forEach((s, i) => s.classList.toggle('is-shown', i === spans.length - 1));
          }
        });
      }
    }

    updateSyncCaption(bubble, el) {
      const audio = bubble._audio;
      const spans = el._syncSpans;
      if (!audio || !spans || !spans.length) return;
      let progress = 0;
      if (audio.duration && audio.duration !== Infinity) {
        progress = audio.currentTime / audio.duration;
      }
      // ROLLENDES FENSTER (Davids Sichtung 2026-07-22): immer nur das
      // AKTUELLE Häppchen zeigen (~1 Satz / ~6 Wörter), nicht den
      // anwachsenden Gesamttext — die Tafel bleibt klein.
      const current = Math.min(spans.length - 1, Math.floor(progress * spans.length));
      spans.forEach((span, i) => {
        span.classList.toggle('is-shown', i === current);
      });
    }

    /* ----------------------------------------------------------
       Standalone Mode (Fallback without MediaManager)
       ---------------------------------------------------------- */

    initStandalone() {
      // Legacy mode: initialize all bubbles immediately
      const allBubbles = document.querySelectorAll('.audio-bubble');
      if (allBubbles.length === 0) return;

      this.bubbles = Array.from(allBubbles);
      this.bubbles.forEach(bubble => this.initBubble(bubble));

      if (window.location.search.includes('debug=story')) {
        console.log('[AudioBubbles] Running in standalone mode (no MediaManager)');
      }
    }
  }

  /* ----------------------------------------------------------
     2. Initialize Module
     ---------------------------------------------------------- */

  // Create module instance
  const audioBubblesModule = new AudioBubblesModule();

  // Expose globally for debugging
  window.StoryModuleAudioBubbles = audioBubblesModule;

})(window);
